"use client";

import { useEffect, useMemo, useRef, useState, type Dispatch, type MouseEvent, type SetStateAction } from "react";
import { z } from "zod";
import { resolveConfiguratorPackageAdvice } from "@/lib/packages/configurator-adapter";
import type { PricingTier } from "@/lib/pricing-bands";
import { useWebMcpTool } from "@/lib/webmcp/react";

type AudienceOption = {
  id: string;
  label: string;
  description: string;
};

type SelectableOption = {
  id: string;
  label: string;
};

export type PackageConfiguratorContent = {
  readonly eyebrow: string;
  readonly title: string;
  readonly intro: string;
  readonly pricingApproxNote: string;
  readonly step1Label: string;
  readonly step1Title: string;
  readonly step2Label: string;
  readonly step2Title: string;
  readonly step3Label: string;
  readonly step3Title: string;
  readonly summaryEyebrow: string;
  readonly planningTierSuffix: string;
  readonly audienceLabel: string;
  readonly servicesLabel: string;
  readonly addOnsLabel: string;
  readonly pricingLabel: string;
  readonly pricingQuoteRequired: string;
  readonly serviceFallback: string;
  readonly addonFallback: string;
  readonly requestCta: string;
  readonly tierLabels: Record<"Starter" | "Growth" | "Enterprise", string>;
  readonly tierNotes: Record<"Starter" | "Growth" | "Enterprise", string>;
  readonly audienceOptions: readonly AudienceOption[];
  readonly serviceOptions: readonly SelectableOption[];
  readonly addonOptions: readonly SelectableOption[];
};

function resolveTierValue<T>(tier: PricingTier, values: Record<PricingTier, T>) {
  if (tier === "Enterprise") {
    return values.Enterprise;
  }

  if (tier === "Growth") {
    return values.Growth;
  }

  return values.Starter;
}

type PackageConfiguratorProps = {
  contactHref: string;
  content: PackageConfiguratorContent;
  pricingBands: Record<PricingTier, string>;
  /** Locale-derived market the displayed bands belong to. */
  market: string;
  /** Currency the displayed bands are denominated in. */
  currency: string;
  /** FX date when the bands are an approximate local-currency conversion. */
  pricingRateDate?: string;
  showLocalCurrencyNote?: boolean;
};

const defaultAudienceId = "agency";
const defaultServiceIds: readonly string[] = ["seo"];
const defaultAddonIds: readonly string[] = ["audit"];
export default function PackageConfigurator({
  contactHref,
  content,
  pricingBands,
  market,
  currency,
  pricingRateDate,
  showLocalCurrencyNote = false,
}: PackageConfiguratorProps) {
  const initialAudienceId = content.audienceOptions.some((option) => option.id === defaultAudienceId)
    ? defaultAudienceId
    : (content.audienceOptions[0]?.id ?? defaultAudienceId);
  const initialServiceIds = defaultServiceIds.filter((id) => content.serviceOptions.some((option) => option.id === id));
  const initialAddonIds = defaultAddonIds.filter((id) => content.addonOptions.some((option) => option.id === id));
  const configuratorRef = useRef<HTMLDivElement>(null);
  const [selectedAudienceId, setSelectedAudienceId] = useState(initialAudienceId);
  const [selectedServiceIds, setSelectedServiceIds] = useState<readonly string[]>(initialServiceIds);
  const [selectedAddonIds, setSelectedAddonIds] = useState<readonly string[]>(initialAddonIds);
  const toolSchema = useMemo(() => {
    const audienceIds = content.audienceOptions.map((option) => option.id) as [string, ...string[]];
    const serviceIds = content.serviceOptions.map((option) => option.id) as [string, ...string[]];
    const addonIds = content.addonOptions.map((option) => option.id) as [string, ...string[]];

    return z
      .object({
        audienceId: z.enum(audienceIds),
        serviceIds: z.array(z.enum(serviceIds)).max(serviceIds.length).refine((ids) => new Set(ids).size === ids.length),
        addonIds: z.array(z.enum(addonIds)).max(addonIds.length).refine((ids) => new Set(ids).size === ids.length),
      })
      .strict();
  }, [content.addonOptions, content.audienceOptions, content.serviceOptions]);
  useWebMcpTool({
    action: "configure_package",
    instance: "packages_configurator",
    schema: toolSchema,
    execute: (input) => {
      const nextRecommendation = resolveConfiguratorPackageAdvice({
        audienceId: input.audienceId,
        serviceIds: input.serviceIds,
        addOnIds: input.addonIds,
        pricing: { market, currency, rateDate: pricingRateDate, bands: pricingBands },
      });

      setSelectedAudienceId(input.audienceId);
      setSelectedServiceIds(input.serviceIds);
      setSelectedAddonIds(input.addonIds);
      return {
        status: "success",
        uiState: {
          ...nextRecommendation,
          audienceId: input.audienceId,
          serviceIds: input.serviceIds,
          addonIds: input.addonIds,
        },
      };
    },
  });
  const selectedAudience = content.audienceOptions.find((option) => option.id === selectedAudienceId) ?? content.audienceOptions[0];
  const recommendation = useMemo(
    () =>
      resolveConfiguratorPackageAdvice({
        audienceId: selectedAudienceId,
        serviceIds: selectedServiceIds,
        addOnIds: selectedAddonIds,
        pricing: { market, currency, rateDate: pricingRateDate, bands: pricingBands },
      }),
    [currency, market, pricingBands, pricingRateDate, selectedAddonIds, selectedAudienceId, selectedServiceIds],
  );
  const tier = recommendation.tier;
  const selectedServiceLabels = content.serviceOptions
    .filter((option) => selectedServiceIds.includes(option.id))
    .map((option) => option.label);
  const selectedAddOnLabels = content.addonOptions
    .filter((option) => selectedAddonIds.includes(option.id))
    .map((option) => option.label);
  const tierLabel = resolveTierValue(tier, content.tierLabels);
  const tierNote = resolveTierValue(tier, content.tierNotes);
  // Fail closed on the shared contract's verdict. A band is shown only when the
  // resolver produced one from a complete pricing context; there is no fallback
  // that would display a price the contract declined to stand behind.
  const tierPricing =
    recommendation.manualQuoteRequired || recommendation.pricingBand === null
      ? content.pricingQuoteRequired
      : recommendation.pricingBand;
  const toggleSelection = (
    event: MouseEvent<HTMLButtonElement>,
    value: string,
    update: Dispatch<SetStateAction<readonly string[]>>,
  ) => {
    event.stopPropagation();
    update((current) => (current.includes(value) ? current.filter((item) => item !== value) : [...current, value]));
  };

  useEffect(() => {
    const configurator = configuratorRef.current;

    configurator?.setAttribute("data-package-react-ready", "true");

    return () => {
      configurator?.removeAttribute("data-package-react-ready");
    };
  }, []);

  return (
    <section
      className="redesign-section redesign-section--muted"
      aria-labelledby="quote-builder-title"
      data-analytics-component-family="pricing_configurator"
      data-analytics-component-id="packages.configurator"
      data-analytics-experience-id="xp.packages.configurator.control"
      data-analytics-variant-key="control"
      data-section-type="pricing"
    >
      <div className="container">
        <div className="marketing-section-heading marketing-section-heading--left">
          <p className="marketing-eyebrow">{content.eyebrow}</p>
          <h2 id="quote-builder-title">{content.title}</h2>
          <p>{content.intro}</p>
          {showLocalCurrencyNote ? (
            <p className="redesign-pricing-note" data-package-pricing-local-currency>
              {content.pricingApproxNote}
            </p>
          ) : null}
        </div>

        <div
          ref={configuratorRef}
          className="package-configurator"
          data-analytics-component-family="pricing_configurator"
          data-analytics-component-id="packages.configurator.controls"
          data-analytics-experience-id="xp.packages.configurator.control"
          data-analytics-variant-key={tier.toLowerCase()}
          data-package-configurator
          data-package-price-enterprise={pricingBands.Enterprise}
          data-package-price-growth={pricingBands.Growth}
          data-package-price-starter={pricingBands.Starter}
          data-package-pricing-fallback={pricingBands.Starter}
          data-package-service-fallback={content.serviceFallback}
          data-package-addon-fallback={content.addonFallback}
          data-package-planning-tier-suffix={content.planningTierSuffix}
          data-package-tier-label-enterprise={content.tierLabels.Enterprise}
          data-package-tier-label-growth={content.tierLabels.Growth}
          data-package-tier-label-starter={content.tierLabels.Starter}
          data-package-tier-note-enterprise={content.tierNotes.Enterprise}
          data-package-tier-note-growth={content.tierNotes.Growth}
          data-package-tier-note-starter={content.tierNotes.Starter}
          data-seoh-xp-depth="none"
          data-seoh-xp-zone="form"
        >
          <div className="package-configurator__steps">
            <fieldset className="package-configurator__step">
              <legend>
                <span>{content.step1Label}</span>
                {content.step1Title}
              </legend>
              <div className="package-configurator__option-grid package-configurator__option-grid--audiences">
                {content.audienceOptions.map((option) => (
                  <button
                    type="button"
                    className={`package-configurator__option${
                      selectedAudienceId === option.id ? " package-configurator__option--active" : ""
                    }`}
                    aria-pressed={selectedAudienceId === option.id}
                    data-analytics-component-family="pricing_configurator"
                    data-analytics-component-id={`packages.configurator.audience.${option.id}`}
                    data-analytics-experience-id="xp.packages.configurator.control"
                    data-analytics-variant-key={tier.toLowerCase()}
                    data-package-audience={option.id}
                    data-package-label={option.label}
                    key={option.id}
                    onClick={() => setSelectedAudienceId(option.id)}
                  >
                    <span>{option.label}</span>
                    <small>{option.description}</small>
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset className="package-configurator__step">
              <legend>
                <span>{content.step2Label}</span>
                {content.step2Title}
              </legend>
              <div className="package-configurator__option-grid">
                {content.serviceOptions.map((option) => {
                  const isSelected = selectedServiceIds.includes(option.id);

                  return (
                    <button
                      type="button"
                      className={`package-configurator__chip${
                        isSelected ? " package-configurator__chip--active" : ""
                      }`}
                      aria-pressed={isSelected}
                      data-analytics-component-family="pricing_configurator"
                      data-analytics-component-id={`packages.configurator.service.${option.id}`}
                      data-analytics-experience-id="xp.packages.configurator.control"
                      data-analytics-variant-key={tier.toLowerCase()}
                      data-package-service={option.id}
                      data-package-label={option.label}
                      key={option.id}
                      onClick={(event) => toggleSelection(event, option.id, setSelectedServiceIds)}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <fieldset className="package-configurator__step">
              <legend>
                <span>{content.step3Label}</span>
                {content.step3Title}
              </legend>
              <div className="package-configurator__option-grid">
                {content.addonOptions.map((option) => {
                  const isSelected = selectedAddonIds.includes(option.id);

                  return (
                    <button
                      type="button"
                      className={`package-configurator__chip${
                        isSelected ? " package-configurator__chip--active" : ""
                      }`}
                      aria-pressed={isSelected}
                      data-analytics-component-family="pricing_configurator"
                      data-analytics-component-id={`packages.configurator.addon.${option.id}`}
                      data-analytics-experience-id="xp.packages.configurator.control"
                      data-analytics-variant-key={tier.toLowerCase()}
                      data-package-addon={option.id}
                      data-package-label={option.label}
                      key={option.id}
                      onClick={(event) => toggleSelection(event, option.id, setSelectedAddonIds)}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          </div>

          <aside
            className="package-configurator__summary"
            aria-live="polite"
            aria-labelledby="package-summary-title"
            data-analytics-component-family="pricing_configurator"
            data-analytics-component-id="packages.configurator.summary"
            data-analytics-experience-id="xp.packages.configurator.control"
            data-analytics-variant-key={tier.toLowerCase()}
          >
            <p className="marketing-eyebrow">{content.summaryEyebrow}</p>
            <h3 id="package-summary-title" data-package-summary-tier key={tier}>
              {tierLabel} {content.planningTierSuffix}
            </h3>
            <p data-package-summary-note>{tierNote}</p>
            <form action={contactHref} method="get" data-package-quote-form>
              <fieldset className="sr-only" aria-label="Package quote request details">
                <input
                  name="audience"
                  type="text"
                  value={selectedAudience.label}
                  readOnly
                  tabIndex={-1}
                  aria-label={content.audienceLabel}
                />
                <input
                  name="services"
                  type="text"
                  value={selectedServiceLabels.length ? selectedServiceLabels.join(", ") : content.serviceFallback}
                  readOnly
                  tabIndex={-1}
                  aria-label={content.servicesLabel}
                />
                <input
                  name="addons"
                  type="text"
                  value={selectedAddOnLabels.length ? selectedAddOnLabels.join(", ") : content.addonFallback}
                  readOnly
                  tabIndex={-1}
                  aria-label={content.addOnsLabel}
                />
                <input
                  name="tier"
                  type="text"
                  value={tierLabel}
                  readOnly
                  tabIndex={-1}
                  aria-label={content.planningTierSuffix}
                />
                <input
                  name="pricing"
                  type="text"
                  value={tierPricing}
                  readOnly
                  tabIndex={-1}
                  aria-label={content.pricingLabel}
                />
              </fieldset>
              <dl>
                <div>
                  <dt>{content.audienceLabel}</dt>
                  <dd data-package-summary-audience>{selectedAudience.label}</dd>
                </div>
                <div>
                  <dt>{content.servicesLabel}</dt>
                  <dd data-package-summary-services>
                    {selectedServiceLabels.length ? selectedServiceLabels.join(", ") : content.serviceFallback}
                  </dd>
                </div>
                <div>
                  <dt>{content.addOnsLabel}</dt>
                  <dd data-package-summary-addons>
                    {selectedAddOnLabels.length ? selectedAddOnLabels.join(", ") : content.addonFallback}
                  </dd>
                </div>
                <div>
                  <dt>{content.pricingLabel}</dt>
                  <dd data-package-summary-pricing>{tierPricing}</dd>
                </div>
              </dl>
              <button
                type="submit"
                className="marketing-button marketing-button--primary"
                data-analytics-component-family="cta"
                data-analytics-component-id={`packages.configurator.cta.${tier.toLowerCase()}.request`}
                data-analytics-cta-id={`cta.packages.configurator.${tier.toLowerCase()}.request`}
                data-analytics-experience-id="xp.packages.configurator.control"
                data-analytics-variant-key={tier.toLowerCase()}
              >
                {content.requestCta}
              </button>
            </form>
          </aside>
        </div>
      </div>
    </section>
  );
}
